/**
 * 收藏页面 —— 展示所有收藏条目。
 */

import { Link } from 'react-router-dom';
import { EntryIcon } from '@/components/EntryIcon';
import type { ContentRepository } from '@/core/contentRepository';
import { useLibraryStore } from '@/store/useLibraryStore';
import { topic } from '@/core/moduleCatalog';
import { ArrowLeft, BookHeart, Heart } from 'lucide-react';

interface FavoritesPageProps {
  repository: ContentRepository;
}

export function FavoritesPage({ repository }: FavoritesPageProps) {
  const favoriteIDs = useLibraryStore((s) => s.favoriteIDs);
  const toggleFavorite = useLibraryStore((s) => s.toggleFavorite);

  const entries = favoriteIDs
    .map((id) => repository.entry(id))
    .filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/" className="text-slate-400 transition-colors hover:text-slate-600">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">收藏</h1>
      </div>

      {entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <BookHeart size={48} className="mb-4 opacity-50" />
          <p className="text-sm">还没有收藏任何条目</p>
          <p className="mt-1 text-xs">浏览百科时点击 ♥ 即可收藏</p>
        </div>
      ) : (
        <div className="space-y-1">
          {entries.map((entry) => {
            const t = topic(entry!.topicID);
            return (
              <Link
                key={entry!.id}
                to={`/topic/${entry!.topicID}#${entry!.id}`}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <EntryIcon entry={entry!} size={36} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                    {entry!.nameZh}
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {t?.title ?? entry!.topicID}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFavorite(entry!.id);
                  }}
                  className="shrink-0 text-red-400 hover:text-red-500"
                >
                  <Heart size={16} fill="currentColor" />
                </button>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
